import { CreateOrderTransactionRequest, EntityId, EntityListResponseCSApiOrderEnriched, EntityListResponseItemAvailabilityAdjustmentViewSummary, EntityListResponseOrderMetadataHistoryEntry, EntityListResponseOrderSummary, EntityListResponseOrderTransaction, EntityListWithAdditionalInfoResponseOrderListOrderAdditionalInfo, EntitySingleResponseOrderMetadata, EntitySingleWithAdditionalInfoResponseOrderOrderAdditionalInfo, OrderBillingInfo, OrderListByFilterRequest, OrderPackagingInfoUpdateRequest, OrderShippingInfo, OrderTransaction, UpdateGatewayResultRequest } from "cs.node.utils/csapi/provisioning";
import { checkResponseStatus } from "@api/Utils";
import axios, { AxiosResponse } from "axios";
import { CurrentFilters } from "@utils/AdvancedFiltersUtils";
import { CSOption } from "@components/Form/CSReactSelect";
import { parseSort } from "@utils/ApiUtils";
import { CSTableServerPagination } from "@components/Table/CSTable";
import { OrderDocumentTypes } from "@utils/OrderUtils";
import { OrderActivity } from "cs.node.utils/OrderUtils";

export const getOrders = async (searchTerm: string, filters:CurrentFilters, pageIndex: number=0, pageSize: number=20, sortObject?: [number | undefined, "ASC" | "DESC"], controller?: AbortController):Promise<EntityListWithAdditionalInfoResponseOrderListOrderAdditionalInfo> =>{
    try {
        const date = filters.find(filter=>filter.id==="date")
        const orderStatuses = filters.find(filter=>filter.id==="status")
        const filterShops = filters.find(filter=>filter.id==="shops")
        const filterSkus = filters.find(filter=>filter.id==="sku")
        const filterDate = (date?.value?? [null, null]) as [Date|null, Date|null] 
        const dateFrom = filterDate[0]
        const dateTo = filterDate[1]
        const filterCustomer = filters.find(filter=>filter.id==="customer")

        const statuses = ((orderStatuses?.value ?? []) as CSOption[]).map(status=>status.value)
        const shopIds = ((filterShops?.value ?? []) as CSOption[]).map(shop=>shop.value)
        const skus = ((filterSkus?.value ?? []) as CSOption[]).map(sku=>sku.value)
        const buyerIdentifier =  ((filterCustomer?.value ) as CSOption)?.value

        let sort: string | undefined
        if(sortObject && sortObject[0] !== undefined && sortObject[1] !== undefined) {
            if(sortObject[0] === 0) {
                sort = `shop_order_id[${sortObject[1]}]`
            }
            if(sortObject[0] === 1) {
                sort = `buyer_name[${sortObject[1]}]`
            }
            if(sortObject[0] === 2) {
                sort = `order_status[${sortObject[1]}]`
            }
            if(sortObject[0] === 3) {
                sort = `date[${sortObject[1]}]`
            }
            if(sortObject[0] === 4) {
                sort = `tot_qty[${sortObject[1]}]`
            }
        }


        const params = {
            pageIndex, 
            pageSize,
            sort,
            dateFrom: dateFrom?.toISOString(),
            dateTo: dateTo?.toISOString(),
            statuses,
            buyerIdentifier,
            skus,
            searchTerm,
            shopIds,
        }
        const response:AxiosResponse<EntityListWithAdditionalInfoResponseOrderListOrderAdditionalInfo,any> = await axios.get('api/orders', {
            params,
            signal: controller?.signal
        })
        return response.data 
    } catch(error: any) {
        checkResponseStatus(error)
        throw error
    }
}

export const getOrdersListByFilter = async(pageIndex?: number, pageSize?: number, sort?: string, withMetadata?: boolean, dateType?: "ORDER_DATE" | "ORDER_CREATION" | "ORDER_UPDATE", dateFrom?: string, dateTo?: string, statuses?: ("OTHER" | "PENDING" | "CONFIRMED" | "CANCELED" | "REFURBISHED" | "SHIPPED" | "PARTIALLY_SHIPPED")[], buyerIdentifier?: string, skus?: string[], searchTerm?: string, shopIds?: string[]):Promise<EntityListWithAdditionalInfoResponseOrderListOrderAdditionalInfo> =>{
    try {
        const params = {
            pageIndex, 
            pageSize,
            sort,
            withMetadata,
            dateType,
            dateFrom,
            dateTo,
            statuses,
            buyerIdentifier,
            skus,
            searchTerm,
            shopIds,
        }
        const response:AxiosResponse<EntityListWithAdditionalInfoResponseOrderListOrderAdditionalInfo,any> = await axios.get('api/orders', {
            params
        })
        return response.data 
    } catch(error: any) {
        checkResponseStatus(error)
        throw error
    }
}

export const getOrdersWithTransactions = async(_pageIndex?: number, _pageSize?: number, _sort?: string, body?: OrderListByFilterRequest, controller?: AbortController): Promise<EntityListResponseCSApiOrderEnriched> =>{
    try{
        const response:AxiosResponse<EntityListResponseCSApiOrderEnriched,any> = await axios.post('api/orders/with-transactions', body, {
            params: {
                _pageIndex,
                _pageSize,
                _sort
            },
            signal: controller?.signal
        })
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const getOrdersByIdList = async (orderIds: EntityId[]):Promise<EntityListResponseOrderSummary> => {
    try{
        const response:AxiosResponse<EntityListResponseOrderSummary,any> = await axios.post('api/orders/list-by-id-list', {
            orderIds
        })
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

//TODO: change parameters to actual API parameter, and convert CurrentFilters in the calling function
export const getCustomerOrders = async ( filters:CurrentFilters, buyerIdentifier:string, pageIndex: number=0, pageSize: number=20):Promise<EntityListWithAdditionalInfoResponseOrderListOrderAdditionalInfo> =>{
    try {
        const date = filters.find(filter=>filter.id==="date")
        const filterDate = (date?.value?? [null, null]) as [Date|null, Date|null] 
        const dateFrom = filterDate[0]
        const dateTo = filterDate[1]

        const params = {
            pageIndex, 
            pageSize,
            dateFrom: dateFrom?.toISOString(),
            dateTo: dateTo?.toISOString(),
            buyerIdentifier
        }
        const response:AxiosResponse<EntityListWithAdditionalInfoResponseOrderListOrderAdditionalInfo,any> = await axios.get('api/orders', {
            params
        })
        return response.data 
    } catch(error: any) {
        checkResponseStatus(error)
        throw error
    }
}

export const getOrder = async (id:string) =>{
    try {
        const response:AxiosResponse<EntitySingleWithAdditionalInfoResponseOrderOrderAdditionalInfo,any> = await axios.get(`api/orders/${id}`)
        return response.data
    } catch(error: any) {
        if(error.response?.status === 404) {
            return undefined
        }
        checkResponseStatus(error)
        throw error
    }
}

export const getAvailabilityAdjustmentsListViewSummary = async (orderId: string, pagination: CSTableServerPagination):Promise<EntityListResponseItemAvailabilityAdjustmentViewSummary> =>{
    try {
        const { _pageIndex, _pageSize, _sort } = pagination
        const sortParsed = parseSort(_sort, ["date","item.sku","shop_id","qty","successful","message"])
        const params = {
            _pageIndex, 
            _pageSize,
            _sort: sortParsed,
            orderId
        }
        const response = await axios.get(`api/orders/availability-adjustments`, {
            params
        })
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const uploadOrderDocument = async(orderId: string, data:FormData) =>{
    try {
        const response = await axios.post(`api/orders/${orderId}/documents`, data)
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const deleteOrderDocument = async(orderId:string, orderDocumentType:OrderDocumentTypes, url:string) =>{
    try {
        const response = await axios.post('api/documents/delete', {
            orderId, orderDocumentType, url
        })
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const printMergedDocuments = async(documents:{url:string, qty:number}[]) =>{
    try {
        const response = await axios.post('api/documents/PDFMerge', {documents}, {
            responseType: 'blob'
        })
        return response
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const saveOrderShippingInfo = async(orderId: string, shippingInfo: OrderShippingInfo) =>{
    try {
        const response = await axios.put(`api/orders/${orderId}/shippingInfo`, {shippingInfo})
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const saveOrderBillingInfo = async(orderId: string, billingInfo: OrderBillingInfo) =>{
    try {
        const response = await axios.put(`api/orders/${orderId}/billingInfo`, {billingInfo})
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const saveOrderPackagingInfo = async(orderId: string, packagingInfo: OrderPackagingInfoUpdateRequest):Promise<EntitySingleWithAdditionalInfoResponseOrderOrderAdditionalInfo> =>{
    try {
        const response = await axios.put(`api/orders/${orderId}/packagingInfo`, {packagingInfo})
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const updateOrderStatus = async(orderId: string, status: string):Promise<string> =>{
    try {
        const response = await axios.put(`api/orders/${orderId}/update-status`, {status})
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const patchOrderAdditionalInfo = async (orderId: string, additional_info: {[key: string]: unknown}):Promise<EntitySingleWithAdditionalInfoResponseOrderOrderAdditionalInfo> =>{
    try {
        const response = await axios.patch(`api/orders/${orderId}/additionalInfo`, {additional_info})
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const getOrderMetadata = async (id:string) =>{
    try {
        const response:AxiosResponse<EntitySingleResponseOrderMetadata,any> = await axios.get(`api/orders/${id}/metadata`)
        return response.data
    } catch(error: any) {
        if(error.response?.status === 404) {
            return undefined
        }
        checkResponseStatus(error)
        throw error
    }
}

export const getOrderMetadataHistory = async (id:string) =>{
    try {
        const response:AxiosResponse<EntityListResponseOrderMetadataHistoryEntry,any> = await axios.get(`api/orders/${id}/metadata/history`)
        return response.data
    } catch(error: any) {
        if(error.response?.status === 404) {
            return undefined
        }
        checkResponseStatus(error)
        throw error
    }
}

export const updateOrderItemVariation = async(orderId: string, sku: string, variation: string, allSiblings: boolean)=>{
    try {
        const response = await axios.patch(`api/orders/${orderId}/update-variation`, {orderId, sku, variation, allSiblings})
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const updateOrderItemQuantity = async(orderId: string, shopId: string, sku: string, quantity: number)=>{
    try {
        const response = await axios.patch(`api/orders/${orderId}/update-quantity`, {orderId, shopId, sku, quantity})
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const exportOrdersExcel = async(body?: OrderListByFilterRequest)=>{
    try{

        const response:AxiosResponse<any,any> = await axios.post('api/orders/export', body, {
            responseType: 'blob'
        })
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const exportOrderExcel = async(orderId: string) =>{
    try{
        const response:AxiosResponse<any,any> = await axios.get(`api/orders/${orderId}/export`, {
            responseType: 'blob'
        })
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const findByShopIdAndShopOrderId = async(shopId: string, shopOrderId: string) =>{
    try{
        const response: AxiosResponse<EntitySingleWithAdditionalInfoResponseOrderOrderAdditionalInfo, any> = await axios.get(`api/orders/${shopId}/shop-order-id/${encodeURIComponent(shopOrderId)}`)
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const getOrderActivities = async(orderId: string) =>{
    try{
        const response: AxiosResponse<OrderActivity[], any> = await axios.get(`api/orders/${orderId}/activities`)
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

export const getOrderTransactions = async(orderId: string,pageIndex?: number, pageSize?: number, sort?: string, controller?: AbortController):Promise<EntityListResponseOrderTransaction> =>{
    try {
        const params = {
            pageIndex, 
            pageSize,
            sort,
        }
        const response:AxiosResponse<EntityListResponseOrderTransaction,any> = await axios.get(`api/orders/${orderId}/transactions`, {
            params,
            signal: controller?.signal
        })
        return response.data 
    } catch(error: any) {
        checkResponseStatus(error)
        throw error
    }
}

export const getRefundableAmount = async(orderId: string):Promise<number> =>{
    try {
        const response:AxiosResponse<number,any> = await axios.get(`api/orders/${orderId}/transactions/refundable-amount`)
        return response.data
    } catch(error: any) {
        checkResponseStatus(error)
        throw error
    }
}

export const createOrderRefund = async(orderId: string, refund: CreateOrderTransactionRequest, gateway_transaction_id: string, fromTransaction: OrderTransaction):Promise<OrderTransaction> => {
    try {
        const response:AxiosResponse<OrderTransaction,any> = await axios.post(`api/orders/${orderId}/transactions/refund`, {
            refund, gateway_transaction_id, fromTransaction
        })
        return response.data
    } catch(error: any) {
        checkResponseStatus(error)
        throw error
    }
}

export const patchTransaction = async(orderId: string, transactionId: string, gatewayUpdateRequest: UpdateGatewayResultRequest, additional_info: {[key: string]: object}):Promise<{status: "ko" | "ko"}> =>{
    try {
        const response:AxiosResponse<{status: "ko" | "ko"},any> = await axios.patch(`api/orders/${orderId}/transactions/${transactionId}`, {
            ...gatewayUpdateRequest,
            additional_info
        })
        return response.data
    } catch(error: any) {
        checkResponseStatus(error)
        throw error
    }
}